import { DashboardResponse } from '@com-delta-idp/mnx-oauth';
import {
  DataLayerAnalytics,
  PageInfo,
  PreviousPageDetails,
  User
} from '@com-delta-idp/mnx-skymiles-landing-page-ui';
import { isMobile } from '@com-delta-idp/mnx-skymiles-shared';
import { enc, SHA256 } from 'crypto-js';
import {
  DATA_ANALYTICS,
  DataLayerPageLoad,
  USER_LOGGED_IN_STATUS
} from '../models';
import {
  getCardLabelByValue,
  getCountry,
  getLanguage,
  getValidValue
} from './analytics-helper.utils';

export const getSystemEnvironment = (): string =>
  isMobile() ? DATA_ANALYTICS.MOBILE : DATA_ANALYTICS.DESKTOP;

export const getPreviousPageDetails = (
  previousUrl?: string | null
): PreviousPageDetails => {
  const url = previousUrl || '';
  if (!url) return { url: '', name: '' };
  return {
    url,
    name:
      url === '/' ? 'dal:home:no-subsection' : `dal${url.split('/').join(':')}`
  };
};

export const getPageLoadTime = (pageLoadStartTime: number): number => {
  return pageLoadStartTime
    ? Number(((Date.now() - pageLoadStartTime) / 1000).toFixed(2))
    : 0;
};

const getPageInfo = (
  prefUI: string,
  pageLoadStart: number,
  previousUrl?: string | null
): Partial<PageInfo> => {
  const previousPageDetails = getPreviousPageDetails(previousUrl);
  return {
    page_url: window.location.pathname,
    previous_page_name: previousPageDetails.name,
    previous_page_url: previousPageDetails.url,
    country: getCountry(prefUI),
    language: getLanguage(prefUI),
    sys_env: getSystemEnvironment(),
    page_load_time: getPageLoadTime(pageLoadStart)
  };
};

const getDataLayerPageInfo = (): Partial<PageInfo> => ({
  legacy_page_name: DataLayerPageLoad.page?.info.legacy_page_name,
  page_name: DataLayerPageLoad.page?.info.page_name,
  page_type: DataLayerPageLoad.page?.info.page_type,
  section: DataLayerPageLoad.page?.info.section,
  sub_section: DataLayerPageLoad.page?.info.sub_section
});

export const getPageInfoDataForAnalytics = (
  analyticsData: DataLayerAnalytics,
  prefUI: string,
  pageLoadStart: number,
  previousUrl?: string | null
): DataLayerAnalytics => ({
  ...analyticsData,
  page: {
    ...analyticsData.page,
    info: {
      ...analyticsData.page?.info,
      ...getPageInfo(prefUI, pageLoadStart, previousUrl),
      ...getDataLayerPageInfo()
    }
  }
});

export const getUserDataForAnalytics = (
  analyticsData: DataLayerAnalytics,
  response: DashboardResponse
): User => {
  const emailAddress = getValidValue(response?.primaryEmailAddress);
  const encryptedEmail = emailAddress
    ? SHA256(emailAddress).toString(enc.Base64)
    : '';
  return {
    ...analyticsData.user,
    profile: {
      skymiles_number: getValidValue(response.smNumber),
      skymiles_tier: getValidValue(response.membershipLevel),
      amex_card_type: getCardLabelByValue(response.smeCardTypes),
      available_miles: getValidValue(response.smBalance),
      email: getValidValue(response.primaryEmailAddress),
      encrypted_email: encryptedEmail,
      email_domain: getValidValue(
        response?.primaryEmailAddress?.split('@')?.[1]
      ),
      company_name: response.adminCompanyName ? response.adminCompanyName : ''
    },
    attributes: {
      ...analyticsData.user.attributes,
      is_logged_in: USER_LOGGED_IN_STATUS.AUTHENTICATED,
      login_state: USER_LOGGED_IN_STATUS.HOT,
      login_method: ''
    }
  };
};

export const getCookieValue = (cookieName: string) => {
  const result = new RegExp(
    '(?:^|; )' + encodeURIComponent(cookieName) + '=([^;]*)'
  ).exec(document.cookie);
  return result ? result[1] : false;
};

export const getLoggedInStatus = (): boolean => {
  const accountTypeCookie = getCookieValue(DATA_ANALYTICS.ACCOUNTTYPE);
  const accountCookie = getCookieValue(DATA_ANALYTICS.ACCOUNT);
  if (accountTypeCookie || accountCookie) {
    return true;
  }
  return false;
};
